import { Inject, Injectable, InjectionToken } from '@angular/core';
import { MatSnackBarRef } from '@angular/material/snack-bar';
import { RoiStore, RoiChangeStore, RoiStatsStore } from '../../stores';
import {
  AddLabelsUseCase,
  RemoveLabelsUseCase,
  ReplaceLabelsUseCase,
  RemoveAllLabelsUseCase,
  UpdateRoiPositionUseCase,
  CreateNewRoiUseCase,
  CreateNewRoiPayload,
} from '@analysis/use-cases';
import { Roi } from '@analysis/domain';
import { ProtocolStore } from '@telespot/protocols/ui';
import { SnackbarService } from '@shared/ui';
import { EMPTY, from, MonoTypeOperatorFunction, Observable, of, pipe, timer } from 'rxjs';
import { catchError, distinctUntilChanged, filter, finalize, map, switchMap, tap } from 'rxjs/operators';
import { concatLatestFrom } from '@ngrx/operators';

export type AddLabelsPayload = {
  roiIds: string[];
  labels: string[];
};

export type RemoveLabelsPayload = { roiIds: string[]; labels: string[] };

export type ReplaceLabelsPayload = { roiIds: string[]; labels: string[] };

export type RemoveAllLabelsPayload = string[];

export type UpdateRoiPositionPayload = { roi: Roi };

export const ADD_LABELS_USE_CASE = new InjectionToken<AddLabelsUseCase>('ADD_LABELS_USE_CASE');
export const REMOVE_LABELS_USE_CASE = new InjectionToken<RemoveLabelsUseCase>('REMOVE_LABELS_USE_CASE');
export const REPLACE_LABELS_USE_CASE = new InjectionToken<ReplaceLabelsUseCase>('REPLACE_LABELS_USE_CASE');
export const REMOVE_ALL_LABELS_USE_CASE = new InjectionToken<RemoveAllLabelsUseCase>('REMOVE_ALL_LABELS_USE_CASE');
export const CREATE_NEW_ROI_USE_CASE = new InjectionToken<CreateNewRoiUseCase>('CREATE_NEW_ROI_USE_CASE');
export const UPDATE_ROI_POSITION_USE_CASE = new InjectionToken<UpdateRoiPositionUseCase>(
  'UPDATE_ROI_POSITION_USE_CASE'
);

const SNACKBAR_DEBOUNCE_MS = 300;

@Injectable()
export class RoiLabelingEffects {
  private snackbarRef: MatSnackBarRef<unknown> | undefined;

  constructor(
    private readonly roiStore: RoiStore,
    private readonly roiChangeStore: RoiChangeStore,
    private readonly roiStatsStore: RoiStatsStore,
    @Inject(ADD_LABELS_USE_CASE)
    private readonly addLabelsUseCase: AddLabelsUseCase,
    @Inject(REMOVE_LABELS_USE_CASE)
    private readonly removeLabelsUseCase: RemoveLabelsUseCase,
    @Inject(REPLACE_LABELS_USE_CASE)
    private readonly replaceLabelsUseCase: ReplaceLabelsUseCase,
    @Inject(REMOVE_ALL_LABELS_USE_CASE)
    private readonly removeAllLabelsUseCase: RemoveAllLabelsUseCase,
    @Inject(UPDATE_ROI_POSITION_USE_CASE)
    private readonly updateRoiPositionUseCase: UpdateRoiPositionUseCase,
    @Inject(CREATE_NEW_ROI_USE_CASE)
    private readonly createNewRoiUseCase: CreateNewRoiUseCase,
    private readonly protocolStore: ProtocolStore,
    private readonly snackbarService: SnackbarService
  ) {
    this.wireSnackbar(this.roiChangeStore.applyingChanges$);
  }

  readonly addLabels = this.roiStore.effect((payload$: Observable<AddLabelsPayload>) =>
    payload$.pipe(
      this.dropWhenInProgress(),
      concatLatestFrom(() => [this.roiStore.rois$, this.protocolStore.categories$]),
      map(([{ roiIds, labels }, rois, categories]) => ({
        rois: roiIds.map((id) => rois[id]).filter((roi): roi is Roi => roi != null),
        labels,
        categories,
      })),
      filter(({ rois }) => rois.length > 0),
      switchMap(({ rois, labels, categories }) => {
        this.roiChangeStore.setApplyingChanges(true);
        return from(this.addLabelsUseCase.execute({ rois, labels, categories })).pipe(
          tap(({ changes, updatedRois, counterDeltas }) => {
            this.roiChangeStore.addChanges(changes);
            this.roiStatsStore.applyCounterDeltas(counterDeltas);
            this.roiStore.upsertRois(updatedRois);
          }),
          catchError((error) => {
            this.roiStore.setError(error.message);
            return EMPTY;
          }),
          finalize(() => this.roiChangeStore.setApplyingChanges(false)),
          map((): void => undefined)
        );
      })
    )
  );

  readonly removeLabels = this.roiStore.effect((payload$: Observable<RemoveLabelsPayload>) =>
    payload$.pipe(
      this.dropWhenInProgress(),
      concatLatestFrom(() => this.roiStore.rois$),
      map(([{ roiIds, labels }, rois]) => ({
        rois: roiIds.map((id) => rois[id]).filter((roi): roi is Roi => roi != null),
        labels,
      })),
      filter(({ rois }) => rois.length > 0),
      switchMap(({ rois, labels }) => {
        this.roiChangeStore.setApplyingChanges(true);
        return from(this.removeLabelsUseCase.execute({ rois, labels })).pipe(
          tap(({ changes, updatedRois, counterDeltas }) => {
            this.roiChangeStore.addChanges(changes);
            this.roiStatsStore.applyCounterDeltas(counterDeltas);
            this.roiStore.upsertRois(updatedRois);
          }),
          catchError((error) => {
            this.roiStore.setError(error.message);
            return EMPTY;
          }),
          finalize(() => this.roiChangeStore.setApplyingChanges(false)),
          map((): void => undefined)
        );
      })
    )
  );

  readonly replaceLabels = this.roiStore.effect((payload$: Observable<ReplaceLabelsPayload>) =>
    payload$.pipe(
      this.dropWhenInProgress(),
      concatLatestFrom(() => this.roiStore.rois$),
      map(([{ roiIds, labels }, rois]) => ({
        rois: roiIds.map((id) => rois[id]).filter((roi): roi is Roi => roi != null),
        labels,
      })),
      filter(({ rois }) => rois.length > 0),
      switchMap(({ rois, labels }) => {
        this.roiChangeStore.setApplyingChanges(true);
        return from(this.replaceLabelsUseCase.execute({ rois, labels })).pipe(
          tap(({ changes, updatedRois, counterDeltas }) => {
            this.roiChangeStore.addChanges(changes);
            this.roiStatsStore.applyCounterDeltas(counterDeltas);
            this.roiStore.upsertRois(updatedRois);
          }),
          catchError((error) => {
            this.roiStore.setError(error.message);
            return EMPTY;
          }),
          finalize(() => this.roiChangeStore.setApplyingChanges(false)),
          map((): void => undefined)
        );
      })
    )
  );

  readonly removeAllLabels = this.roiStore.effect((roiIds$: Observable<RemoveAllLabelsPayload>) =>
    roiIds$.pipe(
      this.dropWhenInProgress(),
      concatLatestFrom(() => this.roiStore.rois$),
      map(([roiIds, rois]) => ({
        rois: roiIds.map((id) => rois[id]).filter((roi): roi is Roi => roi != null),
      })),
      filter(({ rois }) => rois.length > 0),
      switchMap(({ rois }) => {
        this.roiChangeStore.setApplyingChanges(true);
        return from(this.removeAllLabelsUseCase.execute({ rois })).pipe(
          tap(({ changes, updatedRois, counterDeltas }) => {
            this.roiChangeStore.addChanges(changes);
            this.roiStatsStore.applyCounterDeltas(counterDeltas);
            this.roiStore.upsertRois(updatedRois);
          }),
          catchError((error) => {
            this.roiStore.setError(error.message);
            return EMPTY;
          }),
          finalize(() => this.roiChangeStore.setApplyingChanges(false)),
          map((): void => undefined)
        );
      })
    )
  );

  readonly updateRoiPosition = this.roiStore.effect((payload$: Observable<UpdateRoiPositionPayload>) =>
    payload$.pipe(
      this.dropWhenInProgress(),
      concatLatestFrom(() => this.roiStore.rois$),
      switchMap(([{ roi }, rois]) => {
        const originalRoi = rois[roi.id] ?? roi;
        this.roiChangeStore.setApplyingChanges(true);
        return from(this.updateRoiPositionUseCase.execute({ roi, originalRoi })).pipe(
          tap(({ deletedRoi, createdRoi, changes, counterDeltas, removedOriginalId }) => {
            this.roiChangeStore.addChanges(changes);
            this.roiStatsStore.applyCounterDeltas(counterDeltas);
            if (removedOriginalId) {
              this.roiStore.removeRoisByIds([removedOriginalId]);
              this.roiStore.upsertRois(new Map([[createdRoi.id, createdRoi]]));
            } else {
              this.roiStore.upsertRois(
                new Map([
                  [deletedRoi.id, deletedRoi],
                  [createdRoi.id, createdRoi],
                ])
              );
            }
          }),
          catchError((error) => {
            this.roiStore.setError(error.message);
            return EMPTY;
          }),
          finalize(() => this.roiChangeStore.setApplyingChanges(false)),
          map((): void => undefined)
        );
      })
    )
  );

  readonly createNewRoi = this.roiStore.effect((payload$: Observable<CreateNewRoiPayload>) =>
    payload$.pipe(
      this.dropWhenInProgress(),
      switchMap((payload) => {
        this.roiChangeStore.setApplyingChanges(true);
        return from(this.createNewRoiUseCase.execute(payload)).pipe(
          tap(({ roiFindings, changes, updatedRois, counterDeltas }) => {
            this.roiChangeStore.addChanges(changes);
            this.roiStatsStore.applyCounterDeltas(counterDeltas);
            this.roiStore.createRoiWithFindings({ roiFindings, updatedRois });
          }),
          catchError((error) => {
            this.roiStore.setError(error.message);
            return EMPTY;
          }),
          finalize(() => this.roiChangeStore.setApplyingChanges(false)),
          map((): void => undefined)
        );
      })
    )
  );

  private readonly wireSnackbar = this.roiStore.effect((flag$: Observable<boolean>) =>
    flag$.pipe(
      distinctUntilChanged(),
      switchMap((active) => (active ? timer(SNACKBAR_DEBOUNCE_MS).pipe(map(() => true)) : of(false))),
      tap((active: boolean) => {
        this.snackbarRef?.dismiss();
        this.snackbarRef = undefined;
        if (!active) return;
        this.snackbarRef = this.snackbarService.show('analysis.applying_changes', {
          duration: 0,
          dismissible: false,
        });
      })
    )
  );

  private dropWhenInProgress<T>(): MonoTypeOperatorFunction<T> {
    return pipe(
      concatLatestFrom(() => [this.roiChangeStore.applyingChanges$, this.roiChangeStore.revertingChanges$]),
      filter(([, applying, reverting]: [T, boolean, boolean]) => !applying && !reverting),
      map(([payload]: [T, boolean, boolean]) => payload)
    );
  }
}
